import styled from "styled-components"

import { CircularProgress } from "@minuthq/meatball-ui-react"

import { useGetUser } from "src/data/user/hooks/useGetUser"
import { usePatchUserNotificationSettings } from "src/data/user/queries/userQueries"
import {
  TEmailNotificationChannel,
  TEmailNotificationMode,
  TUserEventNotification,
} from "src/data/user/types/userTypes"
import { useTranslate } from "src/i18n/useTranslate"
import { DropdownSelect } from "src/ui/DropdownSelect/DropdownSelect"
import { MTooltip } from "src/ui/MTooltip/MTooltip"

type TNotificationModeOption = {
  label: React.ReactNode
  value: TEmailNotificationMode
}

export function EmailModeSelect({
  notification,
  channel,
}: {
  notification: TUserEventNotification
  channel: TEmailNotificationChannel
}) {
  const { t, langKeys } = useTranslate()
  const user = useGetUser()

  const patchUserNotificationSettings = usePatchUserNotificationSettings({
    userId: user.user_id,
    category: notification.category,
  })

  const options: TNotificationModeOption[] = [
    {
      label: t(langKeys.on),
      value: "on",
    },
    {
      label: t(langKeys.off),
      value: "off",
    },
  ]

  function handleChange(newMode: TEmailNotificationMode) {
    const updatedChannels = notification.channels
      .filter((channel) => channel.editable)
      .map((channel) =>
        channel.type !== "email"
          ? { type: channel.type, mode: channel.mode }
          : { type: "email" as const, mode: newMode }
      )

    patchUserNotificationSettings.mutate({
      path: { user_id: user.user_id, category: notification.category },
      body: {
        events: [{ type: notification.type, channels: updatedChannels }],
      },
    })
  }

  const label = channel.mode === "off" ? t(langKeys.off) : t(langKeys.on)

  const color = channel.mode === "off" ? "default" : "good"

  const isLoading = patchUserNotificationSettings.isLoading

  return (
    <Box>
      <MTooltip
        title={
          !channel.editable
            ? t(langKeys.notifications_settings_option_not_editable_tooltip)
            : ""
        }
      >
        <DropdownSelect<TEmailNotificationMode, TNotificationModeOption>
          options={options}
          selectedValue={channel.mode}
          label={isLoading ? <CircularProgress size="18px" /> : label}
          onChange={({ option }) => {
            handleChange(option.value)
          }}
          size="x-small"
          color={color}
          disabled={!channel.editable || isLoading}
          loading={isLoading}
          showCheckmark={false}
        />
      </MTooltip>
    </Box>
  )
}

const Box = styled.div`
  width: max-content;
`
